import { useEffect, useState } from 'react'
import { fetchProductByPublicCode, fetchProductBySlug } from '../../hooks/runtime/catalogApi'
import type { CatalogProduct } from '../../types'
import type { LinkedProductRef } from './linkedProducts'

export const useLinkedProducts = ({
  productId,
  linkedProductRefs,
}: {
  productId: string | null
  linkedProductRefs: LinkedProductRef[]
}) => {
  const [linkedProducts, setLinkedProducts] = useState<CatalogProduct[]>([])
  const [linkedProductsLoading, setLinkedProductsLoading] = useState(false)

  useEffect(() => {
    if (!productId || linkedProductRefs.length === 0) {
      setLinkedProducts([])
      setLinkedProductsLoading(false)
      return
    }

    const controller = new AbortController()
    let active = true
    setLinkedProductsLoading(true)

    const loadLinkedProducts = async () => {
      const results = await Promise.all(
        linkedProductRefs.map(async (ref) => {
          try {
            return ref.kind === 'publicCode'
              ? await fetchProductByPublicCode(ref.value, controller.signal)
              : await fetchProductBySlug(ref.value, controller.signal)
          } catch {
            return null
          }
        }),
      )

      if (!active || controller.signal.aborted) return

      setLinkedProducts(
        results
          .filter((item): item is CatalogProduct => Boolean(item && item._id && item.slug))
          .filter((item) => item._id !== productId)
          .filter((item, index, array) => array.findIndex((entry) => entry._id === item._id) === index)
          .slice(0, 6),
      )
      setLinkedProductsLoading(false)
    }

    void loadLinkedProducts()
    return () => {
      active = false
      controller.abort()
    }
  }, [linkedProductRefs, productId])

  return { linkedProducts, linkedProductsLoading }
}
